// src/components/DiagnosisDetailDialog.tsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import axios from '../api/axios';

interface UserProfile {
  full_name?: string;
  furigana?: string;
  gender?: string;
  birth_date?: string;
  phone_number?: string;
  address?: string;

  chart_number?: string | null;
}

interface Reservation {
  id: number;
  service: string;
  date: string;
  time: string;
  status: string;
  payment_status?: string | null;
  visit_type: string;
  zoom_join_url?: string;
  diagnosis?: string;
  prescription_text?: string;
  notes?: string;
  user_profile?: UserProfile;
  doctor_name?: string | null;

  chart_number?: string | null;
  patient_chart_number?: string | null;
}

interface Props {
  reservation: Reservation;
  onClose: () => void;
}

const DiagnosisDetailDialog: React.FC<Props> = ({ reservation, onClose }) => {
  const { t } = useTranslation();

  // 診療録(カルテ)PDFを認証付きエンドポイントから取得して表示・保存
  const handleDownloadKarte = async () => {
    try {
      const res = await axios.get(`/receipts/karte/${reservation.id}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      alert(t('mypage.karte_download_failed') || '診療録をダウンロードできませんでした');
    }
  };

  const profile = reservation?.user_profile || {};
  const birthday =
    profile?.birth_date && dayjs(profile.birth_date).isValid()
      ? dayjs(profile.birth_date)
      : null;
  const age = birthday ? dayjs().diff(birthday, 'year') : null;

  const clean = (v: unknown) => {
    const s = (v ?? '') as string;
    return String(s).trim() || null;
  };

  const chartNumber =
    clean(reservation?.chart_number) ??
    clean(reservation?.patient_chart_number) ??
    clean(profile?.chart_number) ??
    null;

  const NA = t('na') || '-';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-2xl w-full shadow-lg overflow-y-auto max-h-[90vh]">
        <h2 className="text-xl font-bold mb-4">{t('mypage.diagnosis_detail')}</h2>

        {/* 患者情報 */}
        <table className="w-full text-sm border border-gray-300 mb-6">
          <tbody>
            <tr>
              <td className="border px-2 py-1 w-1/4">{t('doctor.name')}</td>
              <td className="border px-2 py-1 w-1/4">{profile.full_name || NA}</td>
              <td className="border px-2 py-1 w-1/4">{t('doctor.birthday')}</td>
              <td className="border px-2 py-1 w-1/4">
                {birthday
                  ? `${birthday.format('YYYY-MM-DD')}（${age}${t('doctor.years_old')}）`
                  : NA}
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1">{t('doctor.furigana')}</td>
              <td className="border px-2 py-1">{profile.furigana || NA}</td>
              <td className="border px-2 py-1">{t('doctor.gender')}</td>
              <td className="border px-2 py-1">{profile.gender || NA}</td>
            </tr>
            <tr>
              <td className="border px-2 py-1">{t('doctor.phone')}</td>
              <td className="border px-2 py-1">{profile.phone_number || NA}</td>
              <td className="border px-2 py-1">{t('doctor.address')}</td>
              <td className="border px-2 py-1">{profile.address || NA}</td>
            </tr>
            <tr>
              {/*  カルテ番号 */}
              <td className="border px-2 py-1">{t('doctor.chart_number') || 'カルテ番号'}</td>
              <td className="border px-2 py-1">{chartNumber || NA}</td>
              <td className="border px-2 py-1">{t('doctor.service')}</td>
              <td className="border px-2 py-1">
                {t(`service.${reservation.service}`) || reservation.service}
              </td>
            </tr>
          </tbody>
        </table>

        {/* 診療内容（診断・処方・メモ） */}
        <table className="w-full text-sm border border-gray-300 mb-6">
          <tbody>
            <tr>
              <td className="border px-2 py-1 w-1/4 align-top">{t('doctor.diagnosis')}</td>
              <td className="border px-2 py-1">
                <textarea
                  value={reservation.diagnosis || ''}
                  readOnly
                  className="w-full border rounded p-2 bg-gray-100"
                  rows={3}
                />
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1 align-top">{t('doctor.prescription')}</td>
              <td className="border px-2 py-1">
                <textarea
                  value={reservation.prescription_text || ''}
                  readOnly
                  className="w-full border rounded p-2 bg-gray-100"
                  rows={3}
                />
              </td>
            </tr>
            <tr>
              <td className="border px-2 py-1 align-top">{t('doctor.notes')}</td>
              <td className="border px-2 py-1">
                <textarea
                  value={reservation.notes || ''}
                  readOnly
                  className="w-full border rounded p-2 bg-gray-100"
                  rows={3}
                />
              </td>
            </tr>
          </tbody>
        </table>

        {/* 医師署名 */}
        {reservation.doctor_name && (
          <div className="text-right text-xs text-gray-500 mb-4">
            {t('doctor.doctor_sign') || '医師署名'}: {reservation.doctor_name} {t('doctor.doctor_title_suffix') || '医師'}
          </div>
        )}

        <div className="flex justify-end gap-2">
          {/* カルテは支払い確定時に生成されるため、支払い済みのみダウンロード可 */}
          {reservation.payment_status === 'paid' && (
            <button
              onClick={handleDownloadKarte}
              className="px-4 py-2 border border-coral text-coral rounded-full transition-transform hover:-translate-y-0.5 whitespace-nowrap hover:bg-coral/5"
            >
              {t('mypage.download_karte') || '診療録をダウンロード'}
            </button>
          )}
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gradient-primary text-white rounded-full shadow-card transition-transform hover:-translate-y-0.5 whitespace-nowrap hover:bg-coral"
          >
            {t('mypage.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiagnosisDetailDialog;
